import { VeevaVaultAssetImportStatus } from '@atlas/types'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fileService } from '@/services/file.service'
import {
	type VeevaVaultAssetImport,
	type VeevaVaultAssetImportListParams,
	veevaVaultApi,
} from '@/services/veeva-vault.service'

const REFETCH_INTERVAL_MS = 5000

export const veevaVaultImportsKey = ['veeva-vault-imports'] as const

/**
 * A page of recorded imports, polled while any import, on this page or not, has a file that can still
 * be attempted. The previous page stays on screen while the next one loads.
 */
export const useVeevaVaultImports = (params: VeevaVaultAssetImportListParams) =>
	useQuery({
		queryKey: [...veevaVaultImportsKey, 'list', params],
		queryFn: () => veevaVaultApi.listImports(params),
		placeholderData: keepPreviousData,
		refetchInterval: (q) =>
			q.state.data?.counts[VeevaVaultAssetImportStatus.IN_PROGRESS] ? REFETCH_INTERVAL_MS : false,
	})

/** One import already in hand, kept fresh while it is in progress. */
export const useVeevaVaultImport = (assetImport: VeevaVaultAssetImport | undefined) => {
	const assetCode = assetImport?.files[0].assetCode ?? ''
	return useQuery({
		queryKey: [...veevaVaultImportsKey, 'asset', assetCode],
		queryFn: () => veevaVaultApi.getImport(assetCode),
		enabled: assetImport !== undefined,
		initialData: assetImport,
		refetchInterval: (q) =>
			q.state.data?.status === VeevaVaultAssetImportStatus.IN_PROGRESS ? REFETCH_INTERVAL_MS : false,
	})
}

/**
 * Start an asset code's import; an already recorded one comes back unchanged.
 *
 * A create: the files to record are only known once the server has asked Vault, so it only invalidates.
 */
export const useStartVeevaVaultImport = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (assetCode: string) => veevaVaultApi.startImport(assetCode),
		onSettled: () => queryClient.invalidateQueries({ queryKey: veevaVaultImportsKey }),
	})
}

/** How long a download's hidden frame lives; the browser only needs it until the response headers arrive. */
const DOWNLOAD_FRAME_TTL_MS = 60_000

/**
 * Hand a presigned `attachment` URL to the browser from a hidden frame. Each file gets its own frame
 * because a second navigation of one window cancels a first whose headers have not arrived yet.
 */
const saveFromUrl = (url: string) => {
	const frame = document.createElement('iframe')
	frame.hidden = true
	frame.src = url
	document.body.append(frame)
	setTimeout(() => frame.remove(), DOWNLOAD_FRAME_TTL_MS)
}

/**
 * Presign an imported file's library file and hand it to the browser.
 *
 * Not optimistic: nothing cached changes, the result is a URL only the server can mint. A failure
 * usually means the list on screen is stale (the file was deleted from the library), so it refreshes it.
 */
export const useVeevaVaultFileDownload = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (fileId: string) => fileService.getDownloadUrl(fileId),
		onSuccess: saveFromUrl,
		onError: () => queryClient.invalidateQueries({ queryKey: veevaVaultImportsKey }),
	})
}
